import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { 
  Box, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  useMediaQuery, 
  useTheme,
  AppBar,
  Tabs,
  Tab,
  IconButton,
  Typography,
  Toolbar,
  Snackbar,
  Alert
} from '@mui/material';
import { isUserAdmin } from '../utils/adminUtils';
import {
  Person as PersonIcon,
  Search as SearchIcon,
  Chat as ChatIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  AdminPanelSettings as AdminIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { useAuth } from '../contexts/AuthContext';
import NotificationButton from './NotificationButton';

// Import des composants d'onglets

const drawerWidth = 240;

const DashboardLayout: React.FC = () => {
  useRequireAuth();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  // Afficher le message de succès si présent dans location.state
  useEffect(() => {
    if (location.state?.message) {
      setSnackbarMessage(location.state.message);
      setSnackbarOpen(true);
      // Nettoyer le state pour éviter de réafficher le message
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Vérifier si l'utilisateur est admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }

      try {
        const adminStatus = await isUserAdmin(user.uid, user.email || undefined);
        setIsAdmin(adminStatus);
      } catch (error) {
        console.error('Erreur lors de la vérification admin:', error);
        setIsAdmin(false);
      } finally {
        setCheckingAdmin(false);
      }
    };

    checkAdmin();
  }, [user]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { path: 'profile', label: 'Profil', icon: <PersonIcon /> },
    { path: 'recherche', label: 'Recherche', icon: <SearchIcon /> },
    // { path: 'closet', label: 'Mon Dressing', icon: <CheckroomIcon /> },
    // { path: 'liked', label: 'Robes Likées', icon: <FavoriteIcon /> },
    { path: 'history', label: 'Historique', icon: <HistoryIcon /> },
    { path: 'chat', label: 'Messages', icon: <ChatIcon /> },
    { path: 'settings', label: 'Paramètres', icon: <SettingsIcon /> },
    ...(isAdmin ? [{ path: 'admin', label: 'Administration', icon: <AdminIcon />, external: true, url: '/admin/dashboard' }] : [])
  ];

  const drawer = (
    <List>
      {menuItems.map((item) => (
        <ListItem 
          button 
          key={item.path}
          component={item.external ? 'a' : Link}
          href={item.external ? item.url : undefined}
          to={item.external ? undefined : `/dashboard/${item.path}`}
          selected={item.external ? location.pathname.startsWith('/admin') : location.pathname === `/dashboard/${item.path}`}
          sx={{
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                color: 'common.white',
              },
            },
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemIcon sx={{ color: (item.external ? location.pathname.startsWith('/admin') : location.pathname === `/dashboard/${item.path}`) ? 'white' : 'inherit' }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText primary={item.label} />
        </ListItem>
      ))}
    </List>
  );

  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar position="static" elevation={0} sx={{ backgroundColor: 'background.paper', color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs
            value={location.pathname.split('/').pop() || 'profile'}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minWidth: 'auto',
                padding: '12px 16px',
              },
              '& .Mui-selected': {
                color: 'primary.main',
              },
            }}
          >
            {menuItems.map((item) => (
              <Tab
                key={item.path}
                label={item.label}
                value={item.path}
                component={item.external ? 'a' : Link}
                href={item.external ? item.url : undefined}
                to={item.external ? undefined : `/dashboard/${item.path}`}
                icon={item.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </AppBar>
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Outlet />
        </Box>
        
        {/* Snackbar pour les messages de succès */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbarOpen(false)} 
            severity="success" 
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <img
              src="/images/logo.png"
              alt="Allure"
              style={{
                height: 48,
                width: 'auto',
                backgroundColor: 'transparent',
                mixBlendMode: 'multiply'
              }}
            />
            <Typography variant="h6" sx={{ color: 'primary.main', fontFamily: '"Cormorant Garamond", serif' }}>
              Mon Espace
            </Typography>
          </Box>
        </Box>
        {drawer}
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, backgroundColor: 'background.default' }}>
        {/* AppBar avec notifications */}
        <AppBar 
          position="static" 
          elevation={0}
          sx={{ 
            backgroundColor: 'background.paper',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Toolbar>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Allure
            </Typography>
            <NotificationButton />
          </Toolbar>
        </AppBar>
        
        {/* Contenu principal */}
        <Box sx={{ p: 3 }}>
          <Outlet />
        </Box>
      </Box>
      
      {/* Snackbar pour les messages de succès */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DashboardLayout; 